/**
 * Workspace switcher modal.
 * Centered dialog for switching workspaces.
 */

import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { Check, Building2 } from "lucide-react";
import { KiiDialogLayout } from "../kii/kii-dialog-layout";

interface WorkspaceSwitcherProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkspaceSwitcher({ open, onOpenChange }: WorkspaceSwitcherProps) {
  const { t } = useTranslation();
  const { workspaces, activeWorkspace, isLoading, error, switchWorkspace } =
    useWorkspace();
  const queryClient = useQueryClient();

  const handleSwitchWorkspace = (workspaceId: string) => {
    if (workspaceId === activeWorkspace?.id) {
      onOpenChange(false);
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["workspaceMembers"] });
    switchWorkspace(workspaceId);
    onOpenChange(false);
  };

  return (
    <KiiDialogLayout
      open={open}
      onOpenChange={onOpenChange}
      title={t("workspaceSwitcher.title", "Workspaces")}
      size="sm"
    >
      {isLoading && (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground justify-center">
          <div className="w-4 h-4 border-2 border-border border-t-gray-600 rounded-full animate-spin" />
          <span>{t("workspaceSwitcher.loading")}</span>
        </div>
      )}

      {error && (
        <div className="py-4 text-sm text-red-600 text-center">
          {t("workspaceSwitcher.error")}
        </div>
      )}

      {!isLoading && !error && (
        <div className="space-y-1 mt-4">
          {workspaces.map((workspace) => (
            <button
              key={workspace.id}
              onClick={() => handleSwitchWorkspace(workspace.id)}
              className={`flex items-center justify-between w-full px-3 py-2.5 text-left rounded-lg hover:bg-accent transition-colors ${
                workspace.id === activeWorkspace?.id ? "bg-nara-orange/15" : ""
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <span
                    className={`text-sm block truncate ${
                      workspace.id === activeWorkspace?.id
                        ? "text-nara-orange font-medium"
                        : "text-foreground"
                    }`}
                  >
                    {workspace.name}
                  </span>
                  {workspace.handle && (
                    <span className="text-xs text-muted-foreground font-mono">
                      /{workspace.handle}
                    </span>
                  )}
                </div>
              </div>
              {workspace.id === activeWorkspace?.id && (
                <Check className="h-4 w-4 text-nara-orange shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </KiiDialogLayout>
  );
}
